/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl, defineMessages} from 'react-intl'
import {formatPhoneNumber} from '../../utils/custom-utils'
import {stateOptions} from '@salesforce/retail-react-app/app/components/forms/state-province-options'
import {SHIPPING_COUNTRY_CODES} from '../../constants'
import {flags, zipCodeMsg, nameRegex, addressRegex, cityRegex} from './formConstants'

const messages = defineMessages({
    required: {defaultMessage: 'Required', id: 'use_address_fields.error.required'},
    firstName: {defaultMessage: 'First Name', id: 'use_address_fields.label.first_name'},
    lastName: {defaultMessage: 'Last Name', id: 'use_address_fields.label.last_name'},
    phone: {defaultMessage: 'Phone', id: 'use_address_fields.label.phone'},
    country: {defaultMessage: 'Country', id: 'use_address_fields.label.country'},
    address: {defaultMessage: 'Address', id: 'use_address_fields.label.address'},
    city: {defaultMessage: 'City', id: 'use_address_fields.label.city'},
    state: {defaultMessage: 'State', id: 'use_address_fields.label.state'},
    province: {defaultMessage: 'Province', id: 'use_address_fields.label.province'},
    zipCode: {defaultMessage: 'Zip Code', id: 'use_address_fields.label.zipCode'},
    postalCode: {defaultMessage: 'Postal Code', id: 'use_address_fields.label.postal_code'},
    stateCodeInvalid: {
        defaultMessage: 'Please enter 2-letter state/province.',
        id: 'use_address_fields.error.state_code_invalid'
    },
    preferred: {defaultMessage: 'Set as default', id: 'use_address_fields.label.preferred'}
})

export const getFlagIcon = (countryCode) => {
    if (countryCode && flags[countryCode]) return flags[countryCode]
}

const getPhonePrefix = (countryCodes, countryCode = 'US') => {
    let prefix = countryCode === ('US' || 'CA') ? '+1' : ''
    const countryCodeObj = countryCodes?.find((codeObj) => {
        return codeObj['key_value_string'] === countryCode
    })
    return countryCodeObj?.['c_countryCode'] || prefix
}

export const getZipCodeMessage = (countryCode) => {
    if (countryCode && zipCodeMsg[countryCode]) return zipCodeMsg[countryCode]
}

export const formatZipCode = (value, countryCode) => {
    if (!value) return value
    if (countryCode === 'US') {
        const zipCode = value.replace(/[-]/g, '')
        const zipCodeLength = zipCode.length
        if (zipCodeLength < 6) return zipCode
        else if (zipCodeLength < 10) return `${zipCode.slice(0, 5)}-${zipCode.slice(5)}`
        else return `${zipCode.slice(0, 5)}-${zipCode.slice(5, 9)}`
    } else {
        const zipCode = value.replace(/[ ]/g, '')
        const zipCodeLength = zipCode.length
        if (zipCodeLength < 4) return zipCode
        else if (zipCodeLength < 7) return `${zipCode.slice(0, 3)} ${zipCode.slice(3)}`
        else return `${zipCode.slice(0, 3)} ${zipCode.slice(3, 6)}`
    }
}

const countriesList = (countries, countryCode = 'US') => {
    let country = {}
    if (!countries) {
        return SHIPPING_COUNTRY_CODES
    } else {
        country = countries?.find((country) => {
            return country?.key_value_string === countryCode
        })
        return [
            {
                value: country?.key_value_string,
                label: country?.c_displayName
            }
        ]
    }
}

const countriesStateOptions = (countries, countryCode = 'US', stateOptions) => {
    if (!countries) {
        return stateOptions
    } else {
        let country = countries?.find((country) => {
            return country.key_value_string === countryCode
        })
        const states = country?.c_states && JSON.parse(country?.c_states)
        return Object.keys(states).map((key) => ({
            value: key,
            label: states[key]
        }))
    }
}

/**
 * A React hook that provides the field definitions for an address form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */
export default function useAddressFields({
    form: {
        watch,
        control,
        formState: {errors},
        setValue
    },
    prefix = '',
    countryCodes,
    countries,
    setCountryCode
}) {
    const {formatMessage} = useIntl()

    let countryCode = watch('countryCode')
    const countryObject = JSON.parse(localStorage.getItem('selectedCountryRegionLanguage'))
    countryCode = countryObject?.country?.value || countryCode
    if (countryCode && setCountryCode) {
        setCountryCode(countryCode)
    }

    const fields = {
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage(messages.firstName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'given-name',
            placeholder: formatMessage({
                defaultMessage: 'Please enter your first name',
                id: 'use_address_fields.sample.firstName'
            }),
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_address_fields.error.please_enter_first_name'
                }),
                pattern: {
                    value: nameRegex,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid name.',
                        id: 'use_address_fields.error.valid_name'
                    })
                }
            },
            error: errors[`${prefix}firstName`],
            control,
            setValue
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage(messages.lastName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'family-name',
            placeholder: formatMessage({
                defaultMessage: 'Please enter your last name',
                id: 'use_address_fields.sample.lastName'
            }),
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_address_fields.error.please_enter_last_name'
                }),
                pattern: {
                    value: nameRegex,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid name.',
                        id: 'use_address_fields.error.valid_name'
                    })
                }
            },
            error: errors[`${prefix}lastName`],
            control,
            setValue
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage(messages.phone),
            defaultValue: '',
            type: 'tel',
            autoComplete: 'tel',
            placeholder: formatMessage({
                defaultMessage: 'Please enter your phone number',
                id: 'use_address_fields.sample.phonenumber'
            }),
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_address_fields.error.please_enter_phone_number'
                }),
                minLength: {
                    value: countryCode === 'US' || countryCode === 'CA' ? 14 : '',
                    message: formatMessage({
                        defaultMessage: 'Phone number must be 10 digits long.',
                        id: 'use_address_fields.error.valid_phone_number'
                    })
                }
            },
            phone_prefix: getPhonePrefix(countryCodes, countryCode),
            error: errors[`${prefix}phone`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatPhoneNumber(evt.target.value, countryCode))
                }
            }),
            control,
            setValue
        },
        countryCode: {
            name: `${prefix}countryCode`,
            label: formatMessage(messages.country),
            defaultValue: countryCode || 'US',
            type: 'select',
            flag_image: getFlagIcon(countryCode),
            options: countriesList(countries, countryCode),
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select your country.',
                    id: 'use_address_fields.error.please_select_your_country'
                })
            },
            error: errors[`${prefix}countryCode`],
            control,
            setValue
        },
        address1: {
            name: `${prefix}address1`,
            label: formatMessage(messages.address),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-line1',
            placeholder: formatMessage({
                defaultMessage: 'Please enter your address',
                id: 'use_address_fields.sample.address'
            }),
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your address.',
                    id: 'use_address_fields.error.please_select_your_address'
                }),
                pattern: {
                    value: addressRegex,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid address.',
                        id: 'use_address_fields.error.valid_address'
                    })
                }
            },
            error: errors[`${prefix}address1`],
            control,
            setValue
        },
        city: {
            name: `${prefix}city`,
            label: formatMessage(messages.city),
            defaultValue: '',
            type: 'text',
            placeholder: formatMessage({
                defaultMessage: 'Please enter your city',
                id: 'use_address_fields.sample.city'
            }),
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your city.',
                    id: 'use_address_fields.error.please_select_your_city'
                }),
                pattern: {
                    value: cityRegex,
                    message: formatMessage({
                        defaultMessage: 'Please enter a valid city.',
                        id: 'use_address_fields.error.valid_city'
                    })
                }
            },
            error: errors[`${prefix}city`],
            control,
            setValue
        },
        stateCode: {
            name: `${prefix}stateCode`,
            label: formatMessage(countryCode === 'CA' ? messages.province : messages.state),
            defaultValue: '',
            type: 'select',
            options: [...countriesStateOptions(countries, countryCode, stateOptions)],
            rules: {
                required: formatMessage(
                    {
                        defaultMessage: 'Please select your {stateOrProvince}.',
                        id: 'use_address_fields.error.please_select_your_state_or_province'
                    },
                    {stateOrProvince: countryCode === 'CA' ? 'province' : 'state'}
                )
            },
            error: errors[`${prefix}stateCode`],
            control,
            setValue
        },
        postalCode: {
            name: `${prefix}postalCode`,
            label: formatMessage(countryCode === 'CA' ? messages.postalCode : messages.zipCode),
            defaultValue: '',
            type: 'text',
            autoComplete: 'postal-code',
            placeholder: getZipCodeMessage(countryCode),
            rules: {
                required: formatMessage(
                    {
                        defaultMessage: 'Please enter your {postalOrZip}.',
                        id: 'use_address_fields.error.please_enter_your_postal_or_zip'
                    },
                    {
                        postalOrZip: countryCode === 'CA' ? 'postal code' : 'zip code'
                    }
                ),
                pattern: {
                    value: '',
                    message: formatMessage(
                        {
                            defaultMessage: 'Please enter a valid {postalOrZip} code.',
                            id: 'use_address_fields.error.valid_postal_or_zip'
                        },
                        {
                            postalOrZip: countryCode === 'CA' ? 'postal code' : 'zip code'
                        }
                    )
                }
            },
            error: errors[`${prefix}postalCode`],
            inputProps: ({onChange}) => ({
                inputMode: 'numeric',
                onChange(evt) {
                    onChange(formatZipCode(evt.target.value, countryCode))
                }
            }),
            control,
            setValue
        },
        preferred: {
            name: `${prefix}preferred`,
            label: formatMessage(messages.preferred),
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            rules: {},
            control,
            setValue
        }
    }

    return fields
}
